<template>
  <v-dialog v-model="show" max-width="820" scrollable persistent>
    <v-card v-if="show" class="materials-card">
      <v-toolbar color="primary" dark flat dense>
        <v-icon left>mdi-folder-upload-outline</v-icon>
        <v-toolbar-title class="text-subtitle-1 font-weight-bold">
          Materiais do veículo
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="close" :disabled="saving">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pt-5">
        <!-- Info callout -->
        <div class="materials-callout mb-5">
          <v-icon color="amber darken-2" class="mr-2">mdi-lightbulb-on-outline</v-icon>
          <div class="text-body-2">
            <p class="mb-2">Utilize este espaço para manter os seus materiais sempre atualizados.</p>
            <p class="mb-2">
              Anexe neste módulo os materiais oficiais e atualizados do veículo, como
              tabelas de preços, mídia kits e apresentações comerciais.
            </p>
            <p class="mb-0">
              <strong>Importante:</strong> mantenha apenas documentos vigentes, pois eles serão
              utilizados como referência pelas equipes internas. Cada upload ficará registrado com o
              usuário responsável, data e horário do envio.
            </p>
          </div>
        </div>

        <!-- Mídia kit -->
        <div class="mb-5">
          <div class="upload-label mb-2">
            Mídia kit <span class="red--text">*</span>
          </div>
          <FileUpload
            :key="`media-kit-${resetKey}`"
            target="provider-materials/media-kit"
            @set-upload="mediaKitFiles = $event"
          />
        </div>

        <!-- Tabela de preços -->
        <div class="mb-5">
          <div class="upload-label mb-2 d-flex align-center">
            Tabela de preços <span class="red--text ml-1">*</span>
            <v-tooltip right>
              <template v-slot:activator="{ on, attrs }">
                <v-icon small color="primary" class="ml-2" v-bind="attrs" v-on="on">
                  mdi-information-outline
                </v-icon>
              </template>
              <span>Envie a tabela de preços oficial e vigente do veículo.</span>
            </v-tooltip>
          </div>
          <FileUpload
            :key="`price-table-${resetKey}`"
            target="provider-materials/price-table"
            @set-upload="priceTableFiles = $event"
          />
        </div>

        <!-- Arquivos complementares -->
        <div class="mb-2">
          <div class="upload-label mb-2">
            Arquivos complementares <span class="grey--text text--darken-1 font-italic">(opcional)</span>
          </div>
          <FileUpload
            :key="`complementary-${resetKey}`"
            target="provider-materials/complementary"
            @set-upload="complementaryFiles = $event"
          />
        </div>

        <v-divider class="my-5"></v-divider>

        <!-- Already uploaded materials -->
        <div class="upload-label mb-3">Materiais já enviados</div>

        <div v-if="loadingExisting" class="d-flex align-center grey--text text--darken-1 py-3">
          <v-progress-circular indeterminate size="18" width="2" class="mr-2"></v-progress-circular>
          <span class="text-body-2">Carregando...</span>
        </div>

        <div v-else-if="!existingGroups.length" class="grey--text text--darken-1 text-body-2 py-3">
          Nenhum material enviado ainda.
        </div>

        <template v-else>
          <div v-for="group in existingGroups" :key="group.type" class="mb-4">
            <div class="d-flex align-center mb-2">
              <v-icon small :color="group.color" class="mr-2">{{ group.icon }}</v-icon>
              <span class="text-body-2 font-weight-medium">{{ group.label }}</span>
              <v-chip x-small label class="ml-2" color="grey lighten-3">{{ group.items.length }}</v-chip>
            </div>

            <v-list dense class="py-0 existing-list">
              <v-list-item
                v-for="(file, i) in group.items"
                :key="i"
                class="px-2 existing-item"
              >
                <v-list-item-icon class="mr-2 my-2">
                  <v-icon small>{{ fileIcon(file.name) }}</v-icon>
                </v-list-item-icon>
                <v-list-item-content class="py-1">
                  <v-list-item-title class="text-body-2" :title="file.name">
                    {{ file.name || 'Arquivo' }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="caption">
                    <v-icon x-small class="mr-1">mdi-account-outline</v-icon>{{ file.userName || 'Não informado' }}
                    <span class="mx-1">•</span>
                    <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>{{ file.uploadedOn || '—' }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="my-0">
                  <v-btn
                    icon
                    small
                    color="primary"
                    :loading="downloadingId === file._id"
                    :title="`Baixar ${file.name}`"
                    @click="downloadFile(file)"
                  >
                    <v-icon>mdi-download</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>
        </template>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="px-6 py-3">
        <v-spacer></v-spacer>
        <v-btn text @click="close" :disabled="saving">Fechar</v-btn>
        <v-btn
          color="success"
          elevation="0"
          :loading="saving"
          :disabled="!hasStagedFiles"
          @click="submit"
        >
          <v-icon left small>mdi-cloud-upload-outline</v-icon>
          Enviar materiais
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import FileUpload from '@/interface/FileUpload.vue'
import { showSuccess, showError } from '@/plugins/snackbar-helper'

const TYPE_META = {
  media_kit: { label: 'Mídia kit', icon: 'mdi-briefcase-outline', color: 'indigo' },
  price_table: { label: 'Tabela de preços', icon: 'mdi-cash-multiple', color: 'green darken-1' },
  complementary: { label: 'Materiais complementares', icon: 'mdi-paperclip', color: 'blue-grey' }
}

export default {
  name: 'ProviderMaterialsModal',
  components: { FileUpload },
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      saving: false,
      loadingExisting: false,
      downloadingId: null,
      resetKey: 0,
      mediaKitFiles: [],
      priceTableFiles: [],
      complementaryFiles: [],
      existing: []
    }
  },
  computed: {
    show: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    hasStagedFiles() {
      return this.stagedCount('media_kit') + this.stagedCount('price_table') + this.stagedCount('complementary') > 0
    },
    existingGroups() {
      return Object.keys(TYPE_META)
        .map(type => ({
          type,
          label: TYPE_META[type].label,
          icon: TYPE_META[type].icon,
          color: TYPE_META[type].color,
          items: this.existing.filter(m => m.type === type)
        }))
        .filter(group => group.items.length > 0)
    }
  },
  watch: {
    show(val) {
      if (val) {
        this.resetStaged()
        this.loadExisting()
      }
    }
  },
  methods: {
    stagedCount(type) {
      const list = this.filesForType(type)
      return list.filter(f => f && (f._id || f.id)).length
    },
    filesForType(type) {
      if (type === 'media_kit') return this.mediaKitFiles
      if (type === 'price_table') return this.priceTableFiles
      return this.complementaryFiles
    },
    toMaterial(type, file) {
      return {
        type,
        fileId: file._id || file.id,
        name: file.name || '',
        url: file.url || '',
        size: file.size || null,
        mime: file._type || file.type || ''
      }
    },
    resetStaged() {
      this.mediaKitFiles = []
      this.priceTableFiles = []
      this.complementaryFiles = []
      this.resetKey++
    },
    close() {
      this.show = false
    },
    async loadExisting() {
      this.loadingExisting = true
      try {
        const res = await this.$axios.get('providers/materials')
        this.existing = (res.data && res.data.data) ? res.data.data : []
      } catch (error) {
        console.error('Erro ao carregar materiais:', error)
        this.existing = []
      } finally {
        this.loadingExisting = false
      }
    },
    async submit() {
      if (this.saving || !this.hasStagedFiles) return

      const materials = []
      Object.keys(TYPE_META).forEach(type => {
        this.filesForType(type)
          .filter(f => f && (f._id || f.id))
          .forEach(f => materials.push(this.toMaterial(type, f)))
      })

      if (!materials.length) {
        showError('Nenhum arquivo pronto para envio.')
        return
      }

      this.saving = true
      try {
        await this.$axios.post('providers/materials', { materials })
        showSuccess('Materiais enviados com sucesso!')
        this.resetStaged()
        await this.loadExisting()
        this.$emit('uploaded')
      } catch (error) {
        console.error('Erro ao enviar materiais:', error)
        showError(error.response?.data?.message || 'Erro ao enviar materiais.')
      } finally {
        this.saving = false
      }
    },
    fileIcon(name) {
      const ext = (name || '').split('.').pop().toLowerCase()
      if (['xls', 'xlsx', 'csv'].includes(ext)) return 'mdi-file-excel-outline'
      if (['doc', 'docx'].includes(ext)) return 'mdi-file-word-outline'
      if (['ppt', 'pptx'].includes(ext)) return 'mdi-file-powerpoint-outline'
      if (ext === 'pdf') return 'mdi-file-pdf-box'
      if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return 'mdi-file-image-outline'
      return 'mdi-file-outline'
    },
    async downloadFile(file) {
      if (!file._id && !file.url) return
      this.downloadingId = file._id
      try {
        // Backend returns a presigned S3 URL forcing an "attachment" download.
        let url = file.url
        if (file._id) {
          const res = await this.$axios.get(`providers/materials/${file._id}/download`)
          if (res.data && res.data.data && res.data.data.url) {
            url = res.data.data.url
          }
        }
        const a = document.createElement('a')
        a.href = url
        a.download = file.name || 'download'
        document.body.appendChild(a)
        a.click()
        a.remove()
      } catch (error) {
        console.error('Erro ao baixar arquivo:', error)
        window.open(file.url, '_blank', 'noopener')
      } finally {
        this.downloadingId = null
      }
    }
  }
}
</script>

<style scoped>
.upload-label {
  font-weight: 600;
  font-size: 14px;
  color: #37474f;
}

.materials-callout {
  display: flex;
  align-items: flex-start;
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 6px;
  padding: 14px 16px;
  color: #5d4037;
}

.existing-list {
  border: 1px solid #eceff1;
  border-radius: 6px;
}

.existing-item:not(:last-child) {
  border-bottom: 1px solid #f2f4f6;
}
</style>
